/**
 * FikoDxf Dialog — 2D DXF Dışa Aktarma UI
 * Görünüm seç (üst/ön/yan) → model kenarlarını DXF indir + SVG önizleme.
 */
import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { edgelerToDxf, dxfIndir, DxfGorunum } from './FikoDxf';

@Component({
  selector: 'fikoyt-dxf',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="dx">
    <div class="dx-gor">
      @for (g of gorunumler; track g.id) {
        <button class="dx-gb" [class.aktif]="gorunum===g.id" (click)="gorunumSec(g.id)">{{ g.ad }}</button>
      }
    </div>
    <div class="dx-ad"><label>Dosya adı</label><input [(ngModel)]="dosyaAd"></div>
    <button class="dx-btn primary" (click)="onizle()">👁️ Önizle & Hazırla</button>

    @if (onizleVar) {
      <div class="dx-bilgi">{{ cizgiSayisi }} çizgi · {{ gorunumAd() }} görünüm</div>
      <svg [attr.viewBox]="vb" class="dx-svg" preserveAspectRatio="xMidYMid meet">
        @for (s of segmentler; track $index) {
          <line [attr.x1]="s[0]" [attr.y1]="s[1]" [attr.x2]="s[2]" [attr.y2]="s[3]" stroke="#33e0ff" stroke-width="0.6" vector-effect="non-scaling-stroke"/>
        }
      </svg>
      <button class="dx-btn" (click)="indir()">⬇️ DXF İndir</button>
    }
    @if (hata) { <div class="dx-hata">{{ hata }}</div> }
  </div>
  `,
  styles: [`
    .dx { padding:10px; min-width:280px; max-width:340px; font-size:11px; color:#e6f6ff;
      background:linear-gradient(180deg,rgba(0,40,60,.96),rgba(0,18,36,.98)); border-radius:6px; }
    .dx-gor { display:flex; gap:5px; margin-bottom:8px; }
    .dx-gb { flex:1; padding:6px; background:rgba(0,60,80,.6); border:1px solid rgba(0,200,220,.35); color:#bff; border-radius:5px; cursor:pointer; }
    .dx-gb.aktif { background:linear-gradient(90deg,#0a84c0,#0bb39e); color:#fff; border:none; font-weight:600; }
    .dx-ad { display:flex; flex-direction:column; gap:2px; margin-bottom:6px; }
    .dx-ad label { font-size:9px; color:#5fd9f5; }
    .dx-ad input { background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.35); color:#fff; padding:5px; border-radius:4px; outline:none; }
    .dx-btn { width:100%; padding:8px; margin-top:4px; border:none; border-radius:5px; font-weight:600; cursor:pointer; color:#fff; background:rgba(0,70,90,.8); }
    .dx-btn.primary { background:linear-gradient(90deg,#0a84c0,#0bb39e); }
    .dx-bilgi { margin-top:8px; font-size:10px; color:#9fe8f7; }
    .dx-svg { width:100%; height:200px; background:#06141e; border:1px solid rgba(0,200,220,.25); border-radius:5px; margin:4px 0; }
    .dx-hata { margin-top:8px; padding:6px; background:rgba(255,50,50,.2); border:1px solid rgba(255,100,100,.4); border-radius:4px; color:#ff9a9a; text-align:center; }
  `]
})
export class FikoDxfComponent {
  @Input() anaGosterim: any = null;
  @Input() pencere: any;
  @Input() ayarlar: any = {};
  @Input() modelleme: any = null;

  gorunumler = [{ id: 'ust' as DxfGorunum, ad: 'Üst' }, { id: 'on' as DxfGorunum, ad: 'Ön' }, { id: 'yan' as DxfGorunum, ad: 'Yan' }];
  gorunum: DxfGorunum = 'ust';
  dosyaAd = 'fikoyt-parca.dxf';
  onizleVar = false; cizgiSayisi = 0; hata: string | null = null;
  segmentler: number[][] = []; vb = '0 0 100 100';
  private dxfMetin = '';

  constructor(private cd: ChangeDetectorRef) {}

  gorunumAd(): string { return this.gorunumler.find(g => g.id === this.gorunum)?.ad || ''; }
  gorunumSec(g: DxfGorunum): void { this.gorunum = g; if (this.onizleVar) this.onizle(); else this.cd.detectChanges(); }

  private edgeGruplariBul(): any[] {
    const mesh = this.modelleme?.govdeMesh || this.anaGosterim?.modelleme?.govdeMesh;
    return (mesh as any)?.occt_edgeGroups || [];
  }

  onizle(): void {
    this.hata = null;
    const eg = this.edgeGruplariBul();
    if (!eg.length) { this.hata = 'Model kenarı bulunamadı (önce model oluşturun)'; this.onizleVar = false; this.cd.detectChanges(); return; }
    const r = edgelerToDxf(eg, this.gorunum);
    this.dxfMetin = r.dxf; this.cizgiSayisi = r.cizgiSayisi;
    // SVG önizleme: aynı projeksiyonla segmentler + viewBox (DXF Y yukarı → SVG Y aşağı, ters çevir)
    const segs: number[][] = []; let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const pr = (p: any) => this.gorunum === 'on' ? [p.x, p.z] : this.gorunum === 'yan' ? [p.y, p.z] : [p.x, p.y];
    for (const grup of eg) { const pts = grup?.pts; if (!Array.isArray(pts)) continue;
      for (let i = 1; i < pts.length; i++) { const a = pr(pts[i - 1]), b = pr(pts[i]);
        segs.push([a[0], a[1], b[0], b[1]]); minX = Math.min(minX, a[0], b[0]); maxX = Math.max(maxX, a[0], b[0]); minY = Math.min(minY, a[1], b[1]); maxY = Math.max(maxY, a[1], b[1]); } }
    const w = (maxX - minX) || 1, h = (maxY - minY) || 1, pad = Math.max(w, h) * 0.05;
    // SVG için: X'i sola, Y'yi yukarı-pozitif olacak şekilde ters çevir (CAD konvansiyonu)
    this.segmentler = segs.map(s => [s[0] - minX, maxY - s[1], s[2] - minX, maxY - s[3]].map(v => +v.toFixed(3)));
    this.vb = `${-pad} ${-pad} ${w + 2 * pad} ${h + 2 * pad}`;
    this.onizleVar = true; this.cd.detectChanges();
  }

  indir(): void { if (this.dxfMetin) dxfIndir(this.dxfMetin, this.dosyaAd || 'fikoyt-parca.dxf'); }
}
